// Dropdowns for values whose meaning depends on a type, in the encodings the engine reads:
//   call/destination.lua  AGENT -> agent code, QUEUE -> queue id, IVR -> menu id, PLAYBACK -> sound,
//                         CALLBACK -> "campaign_id[,delay_sec[,sound]]", EXTERNAL / VOICEMAIL / HANGUP -> text
//   ivr/ivr.lua actions   PLAY -> sound, QUEUE -> queue id, SUBMENU -> menu id, GOTO -> menu id | BACK | ROOT,
//                         TRANSFER -> "TYPE:value" (a destination), CALLBACK as above, the rest -> text
// A value that is not in the list (an older row, a deleted queue) stays shown and selected.
import * as React from 'react';
import { Input, Select } from '@/components/ui/input';
import { useRefOptions, rowLabel } from '@/hooks/meta';
import { AudioFilePicker } from '@/components/AudioFilePicker';
import { t, LANGUAGES } from '@/i18n';
import { useAuth, hasModule, type Module, type Tenant } from '@/store/auth';
import { enumLabel } from '@/lib/utils';

type Change = (v: string | null) => void;
type Opt = { value: string; label: string };

export const DEST_TYPES = ['AGENT', 'QUEUE', 'IVR', 'VOICEMAIL', 'EXTERNAL', 'PLAYBACK', 'HANGUP', 'CALLBACK', 'EXTENSION', 'RING_GROUP', 'CONFERENCE', 'PAGING'];
// destination types (and IVR actions) of one product module (migration 026): offered only to tenants that run it
const TYPE_MODULE: Record<string, Module> = { AGENT: 'CALLCENTER', QUEUE: 'CALLCENTER', CALLBACK: 'CALLCENTER',
  EXTENSION: 'PBX', RING_GROUP: 'PBX', CONFERENCE: 'PBX', PAGING: 'PBX' };
export function typeAllowed(type: string, tenant: Tenant | null | undefined): boolean {
  const m = TYPE_MODULE[type];
  return !m || hasModule(tenant, m);
}

function Pick({ name, value, onChange, options, empty }: { name: string; value: string | null | undefined; onChange: Change; options: Opt[]; empty: string }) {
  const v = value ?? '';
  return (
    <Select name={name} value={v} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{empty}</option>
      {v && !options.some((o) => o.value === v) && <option value={v}>{v}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </Select>
  );
}

function Text({ name, value, onChange, placeholder, max }: { name: string; value: string | null | undefined; onChange: Change; placeholder?: string; max?: number }) {
  return <Input name={name} maxLength={max} placeholder={placeholder} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} />;
}

// CALLBACK: pick the campaign, keep any ",delay[,sound]" already typed after it
function CallbackInput({ name, value, onChange }: { name: string; value: string | null | undefined; onChange: Change }) {
  const campaigns = useRefOptions('campaigns');
  const [cid, ...rest] = String(value ?? '').split(',');
  const tail = rest.length ? `,${rest.join(',')}` : '';
  return (
    <div className="flex gap-1">
      <Pick name={name} value={cid || null} empty={t('Choose a campaign…')} onChange={(c) => onChange(c ? `${c}${tail}` : null)}
        options={(campaigns.data ?? []).map((c) => ({ value: String(c.id), label: c.name }))} />
      <Input aria-label={t('Delay and sound')} className="w-40" placeholder=",delay_sec,sound" value={tail}
        onChange={(e) => onChange(cid ? `${cid}${e.target.value}` : null)} disabled={!cid} />
    </div>
  );
}

// the value for one type (destination type or IVR action)
export function TypedValueInput({ name, type, value, onChange, max }: { name: string; type: string | null; value: string | null | undefined; onChange: Change; max?: number }) {
  const ty = type ?? '';
  const agents = useRefOptions('agents', ty === 'AGENT');
  const queues = useRefOptions('queues', ty === 'QUEUE');
  const menus = useRefOptions('ivr-menus', ty === 'IVR' || ty === 'SUBMENU' || ty === 'GOTO');
  const menuOpts = (menus.data ?? []).map((m) => ({ value: String(m.id), label: m.name }));
  // PBX destinations name the number (docs/PBX.md)
  const pbxPath = ({ EXTENSION: 'extensions', RING_GROUP: 'ring-groups', CONFERENCE: 'conference-rooms', PAGING: 'paging-groups' } as Record<string, string>)[ty];
  const pbx = useRefOptions(pbxPath, !!pbxPath);
  if (pbxPath) {
    return <Pick name={name} value={value} onChange={onChange} empty={t('Choose a number…')}
      options={(pbx.data ?? []).map((r) => ({ value: String(r.number), label: `${r.number} · ${r.name ?? ''}` }))} />;
  }
  switch (ty) {
    case 'AGENT':
      return <Pick name={name} value={value} onChange={onChange} empty={t('Choose an agent…')}
        options={(agents.data ?? []).map((a) => ({ value: String(a.agent_code), label: rowLabel(a) }))} />;
    case 'QUEUE':
      return <Pick name={name} value={value} onChange={onChange} empty={t('Choose a queue…')}
        options={(queues.data ?? []).map((q) => ({ value: String(q.id), label: q.name }))} />;
    case 'IVR': case 'SUBMENU':
      return <Pick name={name} value={value} onChange={onChange} empty={t('Choose an IVR menu…')} options={menuOpts} />;
    case 'GOTO':
      return <Pick name={name} value={value} onChange={onChange} empty={t('Choose a menu…')}
        options={[{ value: 'BACK', label: t('Back (previous menu)') }, { value: 'ROOT', label: t('Root (first menu)') }, ...menuOpts]} />;
    case 'PLAYBACK': case 'PLAY':
      return <AudioFilePicker name={name} value={value} onChange={onChange} />;
    case 'CALLBACK':
      return <CallbackInput name={name} value={value} onChange={onChange} />;
    case 'TRANSFER':
      return <DestinationStringInput name={name} value={value} onChange={onChange} />;
    case 'EXTERNAL': return <Text name={name} value={value} onChange={onChange} max={max} placeholder={t('phone number')} />;
    case 'VOICEMAIL': return <VoicemailBoxInput name={name} value={value} onChange={onChange} max={max} />;
    case 'HANGUP': return <Text name={name} value={value} onChange={onChange} max={max} placeholder={t('cause (optional)')} />;
    case 'SAY': return <Text name={name} value={value} onChange={onChange} max={max} placeholder={t('text to say')} />;
    case 'GET_DIGITS': return <Text name={name} value={value} onChange={onChange} max={max} placeholder="variable,min,max,timeout_ms[,sound]" />;
    case 'LANGUAGE': return <LanguageInput name={name} value={value} onChange={onChange} />;
    case 'REPEAT': return <Text name={name} value={value} onChange={onChange} max={max} placeholder={t('(not used)')} />;
    default: return <Text name={name} value={value} onChange={onChange} max={max} />;
  }
}

// VOICEMAIL: a PBX tenant picks one of its voicemail boxes; any other box name (a call-center box such as
// "afterhours", which needs no row) is typed after "Other box…"
function VoicemailBoxInput({ name, value, onChange, max }: { name: string; value: string | null | undefined; onChange: Change; max?: number }) {
  const tenant = useAuth((s) => s.tenant);
  const pbx = hasModule(tenant, 'PBX');
  const boxes = useRefOptions('voicemail-boxes', pbx);
  const [other, setOther] = React.useState(false);
  if (!pbx) return <Text name={name} value={value} onChange={onChange} max={max} placeholder={t('mailbox')} />;
  const list = (boxes.data ?? []).map((b) => ({ value: String(b.box), label: `${b.box} · ${b.name}` }));
  const known = !value || list.some((o) => o.value === value);
  if (other || (!known && !boxes.isLoading)) {
    return (
      <div className="flex gap-1">
        <Text name={name} value={value} onChange={onChange} max={max} placeholder={t('mailbox')} />
        {list.length > 0 && <button type="button" className="text-xs text-primary underline" onClick={() => { setOther(false); onChange(null); }}>{t('List')}</button>}
      </div>
    );
  }
  return (
    <Select name={name} value={value ?? ''} onChange={(e) => (e.target.value === '__other' ? (setOther(true), onChange(null)) : onChange(e.target.value || null))}>
      <option value="">{t('Choose a voicemail box…')}</option>
      {list.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      <option value="__other">{t('Other box…')}</option>
    </Select>
  );
}

// a voice prompt language (IVR action LANGUAGE, an extension's language): the portal's languages, or another
// code (prompts recorded in that language, docs/PHASE26.md) typed after "Other language…"
export function LanguageInput({ name, value, onChange }: { name: string; value: string | null | undefined; onChange: Change }) {
  const [other, setOther] = React.useState(false);
  const known = !value || LANGUAGES.some((l) => l.code === value);
  if (other || !known) {
    return (
      <div className="flex gap-1">
        <Text name={name} value={value} onChange={onChange} max={16} placeholder="fr, pt-BR …" />
        <button type="button" className="text-xs text-primary underline" onClick={() => { setOther(false); onChange(null); }}>{t('List')}</button>
      </div>
    );
  }
  return (
    <Select name={name} value={value ?? ''} onChange={(e) => (e.target.value === '__other' ? (setOther(true), onChange(null)) : onChange(e.target.value || null))}>
      <option value="">{t('(none)')}</option>
      {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      <option value="__other">{t('Other language…')}</option>
    </Select>
  );
}

// "TYPE:value" in one field (queue overflow_destination, IVR exit_destination, IVR TRANSFER action).
// exclude: types not offered here (queue overflow: CALLBACK)
export function DestinationStringInput({ name, value, onChange, exclude }: { name: string; value: string | null | undefined; onChange: Change; exclude?: string[] }) {
  const tenant = useAuth((s) => s.tenant);
  const m = String(value ?? '').match(/^\s*([A-Z_]+)\s*:?\s*(.*)$/);
  const type = m && DEST_TYPES.includes(m[1]) ? m[1] : '';
  const rest = m && type ? m[2] : '';
  const join = (k: string, v: string | null) => (k ? (v ? `${k}:${v}` : k) : null);
  if (value && !type) {   // something this form does not know: keep it editable as text
    return <Text name={name} value={value} onChange={onChange} />;
  }
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-1">
      <Select name={`${name}__type`} aria-label={t('Destination type')} value={type} onChange={(e) => onChange(join(e.target.value, null))}>
        <option value="">{t('(none)')}</option>
        {DEST_TYPES.filter((d) => d === type || (!exclude?.includes(d) && typeAllowed(d, tenant))).map((d) => <option key={d} value={d}>{enumLabel(d)}</option>)}
      </Select>
      {type ? <TypedValueInput name={name} type={type} value={rest || null} onChange={(v) => onChange(join(type, v))} /> : <span />}
    </div>
  );
}
